import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Home, TreePine, HeartPulse, Award, Loader2, ArrowRight, Check } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import CourseCard from "@/components/CourseCard";
import { Seo } from "@/components/Seo";

export default function OfferPage() {
  const [formations, setFormations] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", structure: "", message: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { http.get("/formations").then((r) => setFormations(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await http.post("/contact", { ...form, interest: "devis" });
      toast.success("Demande envoyée", { description: "Nous revenons vers vous sous 48h avec un devis personnalisé." });
      setForm({ name: "", email: "", structure: "", message: "" });
    } catch (err) {
      toast.error("Erreur", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="offer-page">
      <Seo title="Notre offre de formation" description="Formations sur devis pour structures d'accueil et professionnels de la petite enfance. Sessions en présentiel ou à distance, attestation de participation." />
      <section className="container-x pt-16 pb-10">
        <div className="max-w-3xl">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
            Renforcer les compétences de tous les acteurs de l'enfance.
          </h1>
          <p className="mt-6 text-lg text-slate-700 leading-relaxed font-medium">
            Chaque enfant a droit à un accompagnement et à des soins de qualité, quel que soit son environnement.
          </p>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Nos formations donnent aux professionnels de l'enfance des connaissances et des outils fondés sur la
            science — au service du bien-être global, du développement et des apprentissages de chaque enfant.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a href="#devis" data-testid="offer-cta-devis" className="btn-primary">
              Demander un devis personnalisé <ArrowRight size={18} strokeWidth={2} />
            </a>
            <Link to="/formations" data-testid="offer-cta-catalog" className="btn-outline">
              Voir le catalogue
            </Link>
          </div>
        </div>
      </section>

      <section className="container-x py-6">
        <div className="max-w-2xl mb-10">
          <div className="eyebrow mb-3">Pour qui ?</div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Building2, title: "L'Éducation et l'Accueil des Jeunes Enfants", text: "Écoles maternelles, crèches, micro-crèches, haltes-garderies : formez vos équipes avec un cadre commun fondé sur les neurosciences.", slugs: ["neurosciences-fondamentaux", "attachement-securite-affective", "environnement-sain"] },
            { icon: Home, title: "Accueil à domicile", text: "Auxiliaires parentales, gardes à domicile : des formations à votre rythme, directement applicables dans votre quotidien d'accueil.", slugs: ["caregiving-responsif", "crises-colere-neurosciences"] },
            { icon: HeartPulse, title: "L'enfant en milieu de soins", text: "Services de pédiatrie, structures médico-sociales, professionnels de santé : comprendre le développement de l'enfant pour des soins attentifs et adaptés.", slugs: ["sante-nutrition-sommeil", "caregiving-responsif"] },
            { icon: TreePine, title: "Espaces récréatifs & de loisirs", text: "Ludothèques, centres de loisirs, animateurs, espaces de jeu : comprendre le développement de l'enfant pour enrichir chaque temps d'accueil.", slugs: ["importance-du-jeu", "langage-motricite"] },
          ].map((b, i) => (
            <div key={b.title} className="card-soft p-7 flex flex-col" data-testid={`offer-audience-${i}`}>
              <div className="grid place-items-center h-12 w-12 rounded-2xl bg-brand-100 text-brand-700 mb-4">
                <b.icon strokeWidth={1.5} size={22} />
              </div>
              <h3 className="font-display font-bold text-lg text-slate-900 leading-snug">{b.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.text}</p>
              <div className="mt-5 pt-5 border-t border-slate-100">
                <div className="text-[11px] font-bold uppercase tracking-widest text-slate-400 mb-2.5">Formations recommandées</div>
                <div className="flex flex-col gap-2">
                  {b.slugs.map((slug) => {
                    const f = formations.find((x) => x.slug === slug);
                    if (!f) return null;
                    return (
                      <Link key={slug} to={`/formations/${slug}`} data-testid={`offer-reco-${slug}`} className="group flex items-start gap-2 text-sm text-slate-700 hover:text-brand-700">
                        <ArrowRight size={14} className="text-brand-500 shrink-0 mt-1 transition-transform group-hover:translate-x-0.5" strokeWidth={2} />
                        <span className="leading-snug">{f.title}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8 card-soft p-6 flex items-center gap-4 max-w-3xl" data-testid="offer-attestation-note">
          <div className="grid place-items-center h-12 w-12 rounded-2xl bg-yellow-400/20 text-yellow-600 shrink-0">
            <Award strokeWidth={1.5} size={22} />
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">
            <b>Attestation de participation</b> remise à chaque participant en fin de session, à joindre à votre dossier professionnel.
          </p>
        </div>
        <div className="mt-4 card-soft p-6 max-w-3xl border-l-4 border-brand-500" data-testid="offer-experts-note">
          <p className="text-sm text-slate-700 leading-relaxed">
            Les formations Beehive sont conçues par des experts de la petite enfance,
            reconnus pour leur expertise en développement de l'enfant.
            Elles s'appuient sur les connaissances scientifiques les plus récentes et sur l'expérience de terrain.
          </p>
        </div>
      </section>

      <section className="container-x py-6" data-testid="offer-steps-section">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { n: "1", title: "Choisissez", text: "Consultez notre catalogue : choisissez les formations qui correspondent à votre profil et à votre rythme." },
            { n: "2", title: "Recevez votre devis", text: "Décrivez-nous votre contexte. Nous vous répondons sous 48 h avec une proposition adaptée." },
            { n: "3", title: "Formez-vous", text: "Des sessions en présentiel ou à distance, une attestation de participation remise à chaque participant." },
          ].map((s) => (
            <div key={s.n} className="card-soft p-6 flex gap-4 items-start" data-testid={`offer-step-${s.n}`}>
              <div className="grid place-items-center h-10 w-10 rounded-full bg-brand-500 text-white font-display font-bold shrink-0">{s.n}</div>
              <div>
                <h3 className="font-display font-bold text-slate-900">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">{s.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="container-x py-14" data-testid="offer-formations-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow mb-3">La liste des formations</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight max-w-xl">
              Nos formations, du fondamental au spécialisé.
            </h2>
          </div>
          <Link to="/formations" className="text-sm font-semibold text-brand-700 hover:text-brand-800 inline-flex items-center gap-1">
            Voir les fiches détaillées <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {formations.map((f) => <CourseCard key={f.slug} f={f} />)}
        </div>
      </section>

      <section id="devis" className="container-x py-16">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <div>
            <div className="eyebrow mb-3">Devis personnalisé</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Contactez-nous pour un devis personnalisé.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Chaque contexte d'accueil est différent. Décrivez-nous votre situation — professionnel indépendant,
              équipe, structure ou réseau — et nous construisons une proposition adaptée à vos besoins et votre budget.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-700">
              {["Réponse sous 48h", "Proposition adaptée à votre cadre d'accueil", "Sessions individuelles ou en équipe", "Attestation de participation incluse"].map((t) => (
                <li key={t} className="flex gap-3"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" strokeWidth={2} />{t}</li>
              ))}
            </ul>
          </div>
          <form onSubmit={submit} className="card-soft p-8 space-y-4" data-testid="devis-form">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="devis-name" required placeholder="Nom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="devis-email" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <Input data-testid="devis-structure" placeholder="Votre cadre d'accueil (école, crèche, domicile, loisirs…)" value={form.structure} onChange={(e) => setForm({ ...form, structure: e.target.value })} />
            <Textarea data-testid="devis-message" required rows={5} placeholder="Votre besoin : formations souhaitées, nombre de personnes, contexte…" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            <button data-testid="devis-submit" disabled={loading} className="btn-primary w-full disabled:opacity-70">
              {loading ? <Loader2 size={16} className="animate-spin" /> : "Demander mon devis personnalisé"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
